/**
 * GenerateProblemModal — AI 문제 생성(⑥) 요청 + 결과 검수.
 *
 * 계약: BE-20260825-problem-hub-integrated.md §7.
 * 개념 기반 또는 자유 지시로 무에서 새 문제를 출제한다 → 검수(변형 카드 재사용) → 승인 시 은행 등록.
 * 한 번에 1~10개를 뽑아 각각 독립 검수·승인한다(request_uuid 로 묶임).
 *
 * 화면 흐름을 한 모달에 담는다: [요청] → 생성된 후보 목록 → 고른 후보의 검수 카드.
 * 요청과 결과가 다른 화면에 있으면 "방금 만든 것"을 찾아가는 왕복이 생긴다.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Modal, Button, AiPromptInput } from '../common';
import V2DateTime from '../common/V2DateTime';
import { useAuth } from '../../context/AuthContext';
import ConceptPicker from './ConceptPicker';
import VariationReviewCard from './VariationReviewCard';
import {
  requestGeneration, fetchGenerations, fetchGeneration,
  getJobStatusDisplay, getReviewStatusDisplay, getTypeLabel,
  ANSWER_TYPE_OPTIONS, POLLING_STATUSES, JOB_POLL_MS, AUTH_REDIRECT } from './variationApi';

// 생성에는 '유지(keep)'가 없다 — 무에서 만들기 때문(계약 §7-1)
const GEN_ANSWER_TYPES = ANSWER_TYPE_OPTIONS.filter(([v]) => v !== 'keep');

function GenerateProblemModal({ isOpen, onClose, onApproved = null, onOpenProblem = null, onDirtyChange = null }) {
  const { getAccessToken } = useAuth();
  const [mode, setMode] = useState('concept'); // 'concept' | 'prompt'
  const [conceptIds, setConceptIds] = useState([]);
  const [userPrompt, setUserPrompt] = useState('');
  const [count, setCount] = useState(1);
  const [advOpen, setAdvOpen] = useState(false);
  const [optDifficulty, setOptDifficulty] = useState('');
  const [optAnswerType, setOptAnswerType] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [toast, setToast] = useState(null);

  // 이번 세션에서 만든 것(request_uuid)만 좁혀 본다 — 전체 생성 목록은 다른 사람 것까지 섞인다
  const [requestUuid, setRequestUuid] = useState(null);
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  // 열린 검수 카드의 미저장 여부 — 카드는 접히거나 모달이 닫히면 언마운트되며 편집이 사라진다
  const dirtyRef = useRef(false);
  const [pastLoading, setPastLoading] = useState(false);
  const [isPendingView, setIsPendingView] = useState(false); // 이번 요청분이 아니라 전체 검수 대기

  const seqRef = useRef(0);
  const idsRef = useRef([]); // request_uuid 가 없을 때의 폴백 대상(계약상 uuid 는 선택)

  const loadList = useCallback(async ({ silent = false } = {}) => {
    const seq = (seqRef.current += 1);
    if (!silent) setListLoading(true);
    try {
      if (requestUuid) {
        const d = await fetchGenerations({ requestUuid, perPage: 20 }, getAccessToken);
        if (seq !== seqRef.current) return; // [새로 요청] 등으로 무효화된 응답 무시
        setItems(d.items);
      } else if (idsRef.current.length > 0) {
        // uuid 로 묶을 수 없으면 id 별 상세로 채운다 — 없으면 '대기'에서 영원히 멈춘다.
        // 상세 응답에 id 필드가 없을 수 있어(계약 §7이 약속하지 않는다) 우리가 아는 id 로 덮는다.
        const ids = idsRef.current;
        const fetched = await Promise.all(ids.map((gid) => fetchGeneration(gid, getAccessToken).catch(() => null)));
        if (seq !== seqRef.current) return;
        if (fetched.every((d) => d == null)) throw new Error('생성 상태를 읽지 못했습니다.');
        // 실패한 건은 직전 행을 그대로 둔다 — 200 이던 행을 '대기'로 되돌리면 폴링이 되살아나
        // 실패하는 요청을 영원히 반복한다.
        setItems((prev) => fetched.map((d, i) => {
          if (d) return { ...d, generation_id: ids[i] };
          return prev.find((r) => String(r.generation_id ?? r.id) === String(ids[i])) || { generation_id: ids[i], status: 100 };
        }));
      }
    } catch (e) {
      if (seq !== seqRef.current) return;
      // 이 목록이 곧 폴링의 근거다 — 조용히 실패하면 화면은 낡은 채로 남고 사용자는 모른다
      setToast({ type: 'error', text: e.message || '생성 목록을 불러오지 못했습니다.' });
    } finally {
      // 플래그는 이 호출의 것이다 — 다른 호출이 seq 를 올렸다고 스피너를 영원히 남기면 안 된다
      if (!silent) setListLoading(false);
    }
  }, [requestUuid, getAccessToken]);

  useEffect(() => { if (requestUuid) loadList(); }, [requestUuid, loadList]);

  const anyRunning = items.some((it) => POLLING_STATUSES.includes(it.status));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => loadList({ silent: true }), JOB_POLL_MS);
    return () => clearInterval(t);
  }, [anyRunning, loadList]);

  const canRequest = mode === 'concept' ? conceptIds.length > 0 : userPrompt.trim() !== '';

  const handleRequest = async () => {
    if (requesting || !canRequest) return;
    if (!confirmDiscard()) return; // 요청도 열린 검수 카드를 언마운트한다
    setRequesting(true);
    setToast(null);
    // 이전 요청의 배치 핸들을 먼저 버린다 — request_uuid 는 선택이라, 이번 응답에 없으면
    // 지난 uuid 로 폴링이 계속 돌아 새 결과를 옛 배치가 덮어쓴다.
    seqRef.current += 1;
    idsRef.current = [];
    setRequestUuid(null);
    try {
      const options = {};
      if (optDifficulty !== '') options.difficulty = Number(optDifficulty);
      if (optAnswerType !== '') options.answer_type = optAnswerType;
      const d = await requestGeneration({
        mode,
        conceptIds: mode === 'concept' ? conceptIds : undefined,
        userPrompt: userPrompt.trim() ? userPrompt : undefined,
        count,
        options,
      }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setToast({ type: 'info', text: `${d.count ?? count}개를 만들고 있습니다 — 건당 수십 초 걸립니다.` });
      const ids = Array.isArray(d.generation_ids) ? d.generation_ids : [];
      idsRef.current = ids;
      setIsPendingView(false);
      if (ids.length > 0) {
        setItems(ids.map((gid) => ({ generation_id: gid, status: 100 })));
        setOpenId(ids[0]);
      }
      if (d.request_uuid) setRequestUuid(d.request_uuid);
      else if (ids.length > 0) loadList({ silent: true }); // uuid 없이도 폴링·갱신이 돌게
      else {
        // 붙잡을 손잡이가 하나도 없다 — 만들고 있다고만 말하면 빈 화면을 계속 보게 된다
        setToast({
          type: 'error',
          text: '요청은 받았지만 생성 번호를 받지 못했습니다 — 잠시 후 [검수 대기 불러오기]로 확인해 주세요.',
        });
        return;
      }
    } catch (e) {
      setToast({ type: 'error', text: e.message || '생성 요청에 실패했습니다.' });
    } finally {
      setRequesting(false);
    }
  };

  // 미저장 편집을 조용히 버리지 않는다 — 접기·재요청·닫기 세 경로 모두 여길 지난다
  // 확인만 받는다 — 요청·불러오기가 실패하면 카드는 그대로 남는다. 여기서 내리면 편집은
  // 살아 있는데 표시만 풀린다. 해제는 카드가 언마운트되며 스스로 알린다.
  const confirmDiscard = () => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('저장하지 않은 검수 수정이 있습니다. 버리고 계속할까요?');
  };

  const switchOpen = (nextId) => {
    if (openId != null && String(nextId) !== String(openId) && !confirmDiscard()) return;
    setOpenId(nextId); // 해제는 카드의 언마운트 정리가 알린다
  };

  const closeModal = () => {
    if (!confirmDiscard()) return;
    // 생성물은 승인 전까지 문제은행에 없다 — 모달을 닫으면 request_uuid 와 id 목록이 사라져,
    // 되찾는 길은 [검수 대기 불러오기] 하나뿐이다. 그 사실을 닫는 순간에 말해 준다.
    const pending = items.filter((it) => it.review_status == null || it.review_status === 100);
    if (pending.length > 0) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`아직 승인하지 않은 생성물이 ${pending.length}건 있습니다. 닫으면 이 목록은 사라지고, 다시 열어 [검수 대기 불러오기]로만 찾을 수 있습니다. 닫을까요?`)) return;
    }
    onClose();
  };

  /**
   * 검수 대기 불러오기 — 모달을 닫으면 request_uuid 와 id 목록이 사라진다. 그 뒤엔 아직 승인하지
   * 않은 생성물로 돌아갈 길이 없다(좌측 검색은 승인된 문제만 보여 준다). 계약 §7의 목록 필터로
   * 되찾는다.
   */
  const loadPending = async () => {
    if (!confirmDiscard()) return;
    const seq = (seqRef.current += 1); // 진행 중이던 배치 갱신을 먼저 무효화한다
    setPastLoading(true);
    setToast(null);
    try {
      const d = await fetchGenerations({ reviewStatus: 100, perPage: 20 }, getAccessToken);
      if (seq !== seqRef.current) return;
      const rows = d.items || [];
      idsRef.current = rows.map((r) => r.generation_id ?? r.id).filter((v) => v != null);
      setRequestUuid(null);
      setItems(rows);
      setIsPendingView(true);
      setOpenId(null);
      if (rows.length === 0) setToast({ type: 'info', text: '검수 대기 중인 생성이 없습니다.' });
    } catch (e) {
      if (seq === seqRef.current) setToast({ type: 'error', text: e.message || '검수 대기 목록을 불러오지 못했습니다.' });
    } finally {
      setPastLoading(false);
    }
  };

  const reset = () => {
    if (!confirmDiscard()) return;
    seqRef.current += 1; // 진행 중 응답이 지운 목록을 되살리지 않게
    idsRef.current = [];
    setRequestUuid(null);
    setItems([]);
    setIsPendingView(false);
    setOpenId(null);
    setToast(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={closeModal} title="AI 문제 생성" size="xl" maxHeight="88vh">
      <div className="space-y-4">
        {/* 요청 */}
        <div className="rounded-xl border border-gray-200 p-3">
          <div className="mb-2 flex overflow-hidden rounded-full bg-gray-100 text-xs" role="group" aria-label="생성 방식">
            {[['concept', '개념으로'], ['prompt', '지시로']].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setMode(v)}
                aria-pressed={mode === v}
                className={`px-3 py-1 transition-colors ${mode === v ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'concept' && (
            <ConceptPicker
              value={conceptIds}
              onChange={setConceptIds}
              emptyText="출제할 개념을 고르세요"
              listInFlow /* 바로 아래 지시 칸이 붙는다 — 띄우면 그 밑에 깔려 후보가 안 눌린다(2026-09-15 실측, 부품 주석) */
            />
          )}

          {/* 지시 칸은 모드 조건 **밖**에 둔다 — 개념↔지시를 오가도 같은 인스턴스가 유지돼
              입력한 글자가 남는다. 조건부로 감싸거나 key 를 주면 모드를 바꾸는 순간 날아간다. */}
          <AiPromptInput
            className="mt-2"
            label={mode === 'prompt' ? '출제 지시' : '추가 지시(선택)'}
            value={userPrompt}
            onChange={setUserPrompt}
            rows={mode === 'prompt' ? 4 : 2}
            placeholder={mode === 'prompt'
              ? '어떤 문제를 만들지 지시하세요 — 예: 이차함수 최댓값을 구하는 수능형 4점 문제'
              : '추가 지시(선택) — 예: 실생활 상황으로, 그래프 포함'}
            /* 「옵션·규칙이 지시를 이긴다」 안내(BE-20260829-reply-fe-0828.md §4-2 5-1 —
               「공통 확정」이라 생성도 든다). 형제 둘(단건 변형·시험지 AI 복사)과 달리 **상수로 켜지
               않는다** — 그 둘은 강도(level)를 언제나 실어 보내 가리킬 옵션이 사라지는 순간이 없지만,
               생성에는 늘 실리는 옵션이 없다. 고급의 난이도·문제 유형이 둘 다 「지정 안 함」이면
               handleRequest 가 만든 options 는 빈 객체이고, variationApi.requestGeneration 이
               그것을 요청 몸통에서 아예 뺀다. 그 순간 안내는 없는 것을 가리키는 말이 된다 —
               특히 prompt 모드에서는 이 칸이 본문(헤더 `[출제 지시]`)이라 「본문이 옵션 안에서만
               적용된다」로 읽힌다.
               그래서 **실릴 옵션이 있는지**로 켠다. 조건을 advOpen 으로 잡지 않는 이유는 값이
               패널을 접어도 남아 그대로 실리기 때문이다 — 고른 뒤 접으면 옵션은 가는데 안내는
               꺼지는 반대 거짓말이 된다. 판정 기준을 handleRequest(105-106)와 같은 자로 맞춘다.
               ⚠ 붙는 **자리**만 이 한 곳이 다르다: 회신 §6-2 는 나머지 아홉 곳이 프롬프트 말미에
               `[추가 지시 — 요청자] …(충돌하면 규칙·출력 형식·옵션이 우선)` 블록을 붙인다고 하면서
               생성만 「기존 자리 유지」로 뺐다(prompt 모드 = 헤더 `[출제 지시]` 로 그게 본문이고,
               concept 모드 = 헤더 `[추가 지시]`). 그 헤더에도 우선순위 문장이 함께 가는지는
               회신에 없다 — 우선순위 자체는 확정이라 안내는 켜고, 문장의 유무만 다음 왕복에서 묻는다. */
            showOptionPriority={optDifficulty !== '' || optAnswerType !== ''}
          />

          <button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            aria-expanded={advOpen}
            aria-controls="gen-adv"
            className="mt-2 flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700"
          >
            {advOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            고급 — 난이도·문제 유형
          </button>
          {advOpen && (
            <div id="gen-adv" className="mt-1.5 space-y-1.5 rounded-lg bg-gray-50 p-2">
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <span className="w-16 shrink-0">난이도</span>
                <select
                  value={optDifficulty}
                  onChange={(e) => setOptDifficulty(e.target.value)}
                  className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none"
                >
                  <option value="">지정 안 함</option>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <span className="w-16 shrink-0">문제 유형</span>
                <select
                  value={optAnswerType}
                  onChange={(e) => setOptAnswerType(e.target.value)}
                  className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none"
                >
                  <option value="">지정 안 함</option>
                  {GEN_ANSWER_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </label>
              <p className="text-[10px] text-gray-400">유도값이라 100% 보장되지 않습니다 — 결과는 검수에서 확인하세요.</p>
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-gray-600">
              개수
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <Button size="sm" onClick={handleRequest} loading={requesting} disabled={requesting || !canRequest} icon={<Sparkles className="size-3.5" />}>
              문제 만들기
            </Button>
            {items.length > 0 && (
              <Button size="xs" variant="secondary" onClick={reset}>새로 요청</Button>
            )}
            <span className="text-[11px] text-gray-400">승인해야 문제은행에 등록됩니다.</span>
            <button
              type="button"
              onClick={loadPending}
              disabled={pastLoading || requesting}
              className="ml-auto shrink-0 text-[11px] text-primary underline hover:text-primary/70 disabled:opacity-40"
            >
              {pastLoading ? '불러오는 중…' : '검수 대기 불러오기'}
            </button>
          </div>
          <p
            role={toast?.type === 'error' ? 'alert' : 'status'}
            aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
            className={`mt-1.5 text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
          >
            {toast?.text || ''}
          </p>
        </div>

        {/* 결과 목록 + 검수 */}
        {items.length > 0 && (
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">{isPendingView ? '검수 대기 중인 생성' : '생성 결과'}</span>
              <span className="text-[11px] text-gray-400">{items.length}건</span>
              {isPendingView && (
                <span className="text-[10px] text-amber-600">이번 요청분이 아니라 전체 목록입니다 — 다른 사람이 만든 것도 섞입니다.</span>
              )}
              <button
                type="button"
                onClick={() => loadList()}
                disabled={listLoading || (!requestUuid && idsRef.current.length === 0)}
                aria-label="생성 결과 새로고침"
                title="새로고침"
                className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
              >
                <RefreshCw className={`size-3.5 ${listLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <ul className="space-y-1.5">
              {items.map((it, idx) => {
                const gid = it.generation_id ?? it.id;
                const st = getJobStatusDisplay(it.status);
                const rv = getReviewStatusDisplay(it.review_status);
                const open = String(openId) === String(gid);
                const typeLabel = getTypeLabel(it.result?.problem);
                return (
                  <li key={gid ?? idx} className={`rounded-xl border ${open ? 'border-primary/40' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={() => switchOpen(open ? null : gid)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-1.5 p-2 text-left"
                    >
                      {open ? <ChevronDown className="size-3.5 shrink-0 text-primary" /> : <ChevronRight className="size-3.5 shrink-0 text-gray-300" />}
                      <span className="shrink-0 text-[10px] text-gray-400">{idx + 1}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                      {typeLabel && <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] text-indigo-700">{typeLabel}</span>}
                      {rv && it.review_status !== 100 && (
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${rv.cls}`}>{rv.text}</span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-xs text-gray-500">{it.description || ''}</span>
                      {it.created_at && (
                        <V2DateTime value={it.created_at} className="shrink-0 text-[10px] text-gray-400" />
                      )}
                    </button>
                    {open && gid != null && (
                      <div className="border-t p-3">
                        <VariationReviewCard
                          kind="generation"
                          id={gid}
                          onApproved={onApproved}
                          onOpenProblem={onOpenProblem}
                          onDirtyChange={(d) => { dirtyRef.current = d; onDirtyChange?.(d); }}
                          onChanged={() => loadList({ silent: true })}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default GenerateProblemModal;
